import { createApi } from "@reduxjs/toolkit/query/react";
import { adminBaseQuery } from "@store/baseQuery";

// Type definitions for QR Code Pixel Share entity based on ACTUAL API response
export interface QrCodePixelShare {
  id: number;
  key: string;
  full_name: string;
  destination_link: string;
  opened: number; // 0 or 1
  create_user_id: number;
  created_at: string;
  updated_at?: string;
  qrcode_link: string;
}

export interface CreateQrCodePixelShareRequest {
  key: string;
  full_name?: string | null;
  destination_link: string;
  opened?: boolean | null;
}

export interface UpdateQrCodePixelShareRequest {
  key: string;
  full_name?: string | null;
  destination_link: string;
  opened?: boolean | null;
}

export interface ErrorResponse {
  status: boolean;
  message: string;
  errors?: Record<string, string[]>;
}

// Query parameters interface for filtering and searching
export interface QrCodePixelShareQueryParams {
  search?: string;
  per_page?: number;
  page?: number;
}

// Paginated response interface (based on existing patterns in the codebase)
export interface PaginatedQrCodePixelShareResponse {
  current_page: number;
  data: QrCodePixelShare[];
  last_page: number;
  per_page: number;
  total: number;
}

export const qrCodesApi = createApi({
  reducerPath: "qrCodesApi",
  baseQuery: adminBaseQuery,
  tagTypes: ["QrCodePixelShare"],
  endpoints: (builder) => ({
    // Get all QR code pixel shares
    getQrCodes: builder.query<PaginatedQrCodePixelShareResponse, QrCodePixelShareQueryParams | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        
        // Add query parameters if they exist and params is not void
        if (params?.search) searchParams.append('search', params.search);
        if (params?.per_page) searchParams.append('per_page', params.per_page.toString());
        if (params?.page) searchParams.append('page', params.page.toString());
        
        const queryString = searchParams.toString();
        return `/qrcode-pixel-share${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ["QrCodePixelShare"],
    }),

    // Get a specific QR code pixel share
    getQrCode: builder.query<QrCodePixelShare, number>({
      query: (id) => `/qrcode-pixel-share/${id}`,
      providesTags: (result, error, id) => [
        { type: "QrCodePixelShare", id },
      ],
    }),

    // Create a new QR code pixel share
    createQrCode: builder.mutation<
      QrCodePixelShare,
      CreateQrCodePixelShareRequest
    >({
      query: (data) => ({
        url: "/qrcode-pixel-share",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["QrCodePixelShare"],
    }),

    // Update a specific QR code pixel share
    updateQrCode: builder.mutation<
      QrCodePixelShare,
      { id: number; data: UpdateQrCodePixelShareRequest }
    >({
      query: ({ id, data }) => ({
        url: `/qrcode-pixel-share/${id}`,
        method: "PUT",
        body: { ...data, id },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "QrCodePixelShare", id },
        "QrCodePixelShare",
      ],
    }),

    // Delete a specific QR code pixel share
    deleteQrCode: builder.mutation<
      { qr_code_pixel_share_id: number },
      number
    >({
      query: (id) => ({
        url: `/qrcode-pixel-share/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["QrCodePixelShare"],
    }),
  }),
});

export const {
  useGetQrCodesQuery,
  useGetQrCodeQuery,
  useCreateQrCodeMutation,
  useUpdateQrCodeMutation,
  useDeleteQrCodeMutation,
} = qrCodesApi; 